import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Button,
  Card,
  CardContent,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Label,
  RadioGroup,
  RadioGroupItem,
  Tooltip,
  Typography,
} from "@altimateai/lego";
import { LoadingButton, Stack } from "@altimateai/lego";
import { IconChevronLeft, IconClock, IconBolt } from "@tabler/icons-react";
import { getIconByDatastoreType } from "../../DatastoreImage";
import { DatastoreTypeEnum } from "../../../types";
import { cn } from "@/lib/utils";
import { buildCronExpression, parseCronExpression } from "./scheduleUtils";
import { ScheduleFields } from "./ScheduleFields";

export const SyncType = {
  SCHEDULED: "scheduled",
  REAL_TIME: "real_time",
} as const;

const scheduleSchema = z.object({
  sync_type: z.enum(["scheduled", "real_time"]),
  schedule_frequency: z.string().default("daily"),
  schedule_time: z.string().default("12:00 AM"),
  schedule_day_of_week: z.string().default("1"),
  schedule_day_of_month: z.string().default("1"),
});

type ScheduleFormValues = z.infer<typeof scheduleSchema>;

export interface ScheduleData {
  sync_type: "scheduled" | "real_time";
  schedule_cron: string;
}

interface DbtCloudSyncScheduleProps {
  scheduleData?: Partial<ScheduleData>;
  handleBack: () => void;
  handleFinish: (data: ScheduleData) => void;
  isCreating: boolean;
}

const DbtCloudSyncSchedule = ({
  scheduleData,
  handleBack,
  handleFinish,
  isCreating,
}: DbtCloudSyncScheduleProps): JSX.Element => {
  // Parse existing cron to get initial frequency/time values
  const initialSchedule = parseCronExpression(scheduleData?.schedule_cron || "0 0 * * *");

  const form = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleSchema),
    defaultValues: {
      sync_type: scheduleData?.sync_type || SyncType.SCHEDULED,
      schedule_frequency: initialSchedule.frequency,
      schedule_time: initialSchedule.time,
      schedule_day_of_week: initialSchedule.dayOfWeek,
      schedule_day_of_month: initialSchedule.dayOfMonth,
    },
  });

  const syncType = form.watch("sync_type");

  const handleSubmit = (values: ScheduleFormValues) => {
    // Convert frequency/time to cron expression for the API
    const schedule_cron = buildCronExpression(
      values.schedule_frequency,
      values.schedule_time,
      values.schedule_day_of_week,
      values.schedule_day_of_month
    );
    handleFinish({
      sync_type: values.sync_type,
      schedule_cron,
    });
  };

  const icon = getIconByDatastoreType({
    datastoreType: DatastoreTypeEnum.DBT_CLOUD,
    className: "al-w-10 al-h-10 al-mr-3",
  });

  return (
    <Card>
      <CardContent>
        <Stack direction="row" align="center" className="al-mb-6 al-pt-6">
          {icon}
          <div>
            <Typography variant="subheading">Sync Schedule</Typography>
            <Typography size="sm" className="al-text-muted-foreground">
              Choose how often to sync artifacts from dbt Cloud
            </Typography>
          </div>
        </Stack>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="al-space-y-6">
            {/* Sync Type Selection */}
            <FormField
              control={form.control}
              name="sync_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sync Mode</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="al-grid al-grid-cols-2 al-gap-4 al-mt-2"
                    >
                      <Label
                        htmlFor="sync-scheduled"
                        className={cn(
                          "al-border al-rounded-lg al-p-4 al-transition-all al-block al-font-normal al-cursor-pointer",
                          field.value === SyncType.SCHEDULED
                            ? "al-border-primary al-bg-primary/5"
                            : "al-border-border hover:al-border-primary/50"
                        )}
                      >
                        <Stack direction="row" gap="md" className="al-items-start">
                          <RadioGroupItem
                            value={SyncType.SCHEDULED}
                            id="sync-scheduled"
                            className="al-mt-1"
                          />
                          <div className="al-flex-1">
                            <Stack direction="row" align="center" gap="sm" className="al-mb-2">
                              <IconClock
                                size={24}
                                className={
                                  field.value === SyncType.SCHEDULED
                                    ? "al-text-primary"
                                    : "al-text-muted-foreground"
                                }
                              />
                              <Typography weight="regular">Scheduled</Typography>
                            </Stack>
                            <Typography size="sm" className="al-text-muted-foreground">
                              Sync artifacts on a regular schedule using cron expressions
                            </Typography>
                          </div>
                        </Stack>
                      </Label>

                      <Tooltip title="Coming soon">
                        <Label
                          htmlFor="sync-realtime"
                          className={cn(
                            "al-border al-rounded-lg al-p-4 al-transition-all al-block al-font-normal al-opacity-50 al-cursor-not-allowed",
                            "al-border-border"
                          )}
                        >
                          <Stack direction="row" gap="md" className="al-items-start">
                            <RadioGroupItem
                              value={SyncType.REAL_TIME}
                              id="sync-realtime"
                              className="al-mt-1"
                              disabled
                            />
                            <div className="al-flex-1">
                              <Stack direction="row" align="center" gap="sm" className="al-mb-2">
                                <IconBolt size={24} className="al-text-muted-foreground" />
                                <Typography weight="regular">Real-time</Typography>
                              </Stack>
                              <Typography size="sm" className="al-text-muted-foreground">
                                Sync artifacts immediately when new runs complete
                              </Typography>
                            </div>
                          </Stack>
                        </Label>
                      </Tooltip>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Scheduled Options */}
            {syncType === SyncType.SCHEDULED && (
              <div className="al-space-y-4 al-p-4 al-bg-muted/30 al-rounded-lg">
                <Typography variant="body" size="xs" weight="regular">
                  Schedule Configuration
                </Typography>
                <ScheduleFields form={form} />
              </div>
            )}

            <Typography size="sm" className="al-text-muted-foreground al-pt-2">
              After creation, projects and environments will be discovered automatically. You can
              then enable specific environments for artifact syncing.
            </Typography>

            <Stack justify="between" className="al-pt-6">
              <Button type="button" variant="outline" onClick={handleBack}>
                <IconChevronLeft size={16} />
                Back
              </Button>
              <LoadingButton type="submit" loading={isCreating}>
                Create Connection
              </LoadingButton>
            </Stack>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default DbtCloudSyncSchedule;
